import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import VisitCustomerFields from './VisitCustomerFields';

const emptyForm = {
    visitType: 'existingClient',
    externalClientId: '',
    externalClientBusinessUnit: '',
    clientName: '',
    crmCompany: null,
    crmOpportunity: null,
    prospectName: '',
    areaName: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    address: '',
    objective: '',
    preparationNotes: ''
};

const cleanPayload = (form) => {
    const payload = {
        visitType: form.visitType,
        contactName: form.contactName.trim() || undefined,
        contactPhone: form.contactPhone.trim() || undefined,
        contactEmail: form.contactEmail.trim() || undefined,
        address: form.address.trim() || undefined,
        objective: form.objective.trim(),
        preparationNotes: form.preparationNotes.trim() || undefined
    };

    if (form.visitType === 'existingClient') {
        payload.externalClientId = form.externalClientId;
        payload.externalClientBusinessUnit = form.externalClientBusinessUnit;
    } else if (form.visitType === 'prospect') {
        payload.prospectName = form.prospectName.trim();
        payload.crmCompany = form.crmCompany || null;
        payload.crmOpportunity = form.crmOpportunity || null;
    } else {
        payload.areaName = form.areaName.trim();
    }

    return Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined && value !== ''));
};

const UnplannedVisitDialog = ({
    visible,
    customers,
    loadingCustomers,
    customerLoadError,
    onReloadCustomers,
    loading,
    onHide,
    onSubmit
}) => {
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!visible) return;
        setForm(emptyForm);
        setErrors({});
    }, [visible]);

    const updateField = (field, value) => {
        setForm((current) => {
            const next = { ...current, [field]: value };
            if (field === 'visitType') {
                next.externalClientId = '';
                next.externalClientBusinessUnit = '';
                next.clientName = '';
                next.prospectName = '';
                next.crmCompany = null;
                next.crmOpportunity = null;
                next.areaName = '';
                next.contactName = '';
                next.contactPhone = '';
                next.contactEmail = '';
                next.address = '';
            }
            return next;
        });
        setErrors((current) => ({ ...current, [field]: null }));
    };

    const updateCustomer = (customer) => {
        setForm((current) => ({
            ...current,
            externalClientId: customer?.quickBooksId || '',
            externalClientBusinessUnit: customer?.businessUnit || '',
            clientName: customer?.displayName || '',
            contactName: customer?.contactName || '',
            contactPhone: customer?.contactPhone || '',
            contactEmail: customer?.contactEmail || '',
            address: customer?.address || ''
        }));
        setErrors((current) => ({
            ...current,
            externalClientId: null,
            externalClientBusinessUnit: null
        }));
    };

    const validate = () => {
        const nextErrors = {};
        if (!form.visitType) nextErrors.visitType = 'Selecciona el tipo de visita.';
        if (
            form.visitType === 'existingClient' &&
            (!form.externalClientId || !form.externalClientBusinessUnit)
        ) {
            nextErrors.externalClientId = 'Selecciona un cliente de QuickBooks.';
        }
        if (form.visitType === 'prospect' && !form.prospectName.trim()) nextErrors.prospectName = 'El nombre del prospecto o empresa es obligatorio.';
        if (form.visitType === 'areaProspecting' && !form.areaName.trim()) nextErrors.areaName = 'Indica la zona, ruta o sector del recorrido.';
        if (!form.objective.trim()) nextErrors.objective = 'El objetivo de la visita es obligatorio.';
        if (form.objective.trim().length > 1000) nextErrors.objective = 'Máximo 1000 caracteres.';
        if (form.preparationNotes.trim().length > 2000) nextErrors.preparationNotes = 'Máximo 2000 caracteres.';
        if (form.address.trim().length > 500) nextErrors.address = 'Máximo 500 caracteres.';
        if (form.contactName.trim().length > 180) nextErrors.contactName = 'Máximo 180 caracteres.';
        if (form.contactPhone.trim().length > 80) nextErrors.contactPhone = 'Máximo 80 caracteres.';
        if (
            form.contactEmail.trim() &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail.trim())
        ) {
            nextErrors.contactEmail = 'Ingresa un correo electrónico válido.';
        }
        if (form.prospectName.trim().length > 180) nextErrors.prospectName = 'Máximo 180 caracteres.';
        if (form.areaName.trim().length > 240) nextErrors.areaName = 'Máximo 240 caracteres.';

        setErrors(nextErrors);
        return !Object.keys(nextErrors).length;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        onSubmit(cleanPayload(form));
    };

    const error = (field) => (errors[field] ? <small className="p-error block mt-1">{errors[field]}</small> : null);

    const footer = (
        <div className="flex flex-column sm:flex-row justify-content-end gap-2">
            <Button type="button" label="Cancelar" className="p-button-text" onClick={onHide} disabled={loading} />
            <Button type="button" label={loading ? 'Obteniendo ubicación...' : 'Iniciar visita'} icon="pi pi-play" onClick={handleSubmit} loading={loading} disabled={loading} />
        </div>
    );

    return (
        <Dialog className="ventas-dialog ventas-visit-dialog" header="Iniciar visita no planificada" visible={visible} onHide={onHide} modal draggable={false} footer={footer} style={{ width: '40rem' }} breakpoints={{ '960px': '75vw', '640px': '96vw' }}>
            <div className="grid p-fluid ventas-visit-form">
                <VisitCustomerFields
                    form={form}
                    customers={customers}
                    loadingCustomers={loadingCustomers}
                    customerLoadError={customerLoadError}
                    onReloadCustomers={onReloadCustomers}
                    onFieldChange={updateField}
                    onCustomerChange={updateCustomer}
                    error={error}
                />
                <div className="col-12">
                    <label className="block font-medium mb-2">{form.visitType === 'areaProspecting' ? 'Objetivo del recorrido' : 'Objetivo de la visita'}</label>
                    <InputTextarea value={form.objective} rows={3} maxLength={1000} autoResize onChange={(event) => updateField('objective', event.target.value)} />
                    {error('objective')}
                </div>
                <div className="col-12">
                    <label className="block font-medium mb-2">Notas opcionales</label>
                    <InputTextarea value={form.preparationNotes} rows={3} maxLength={2000} autoResize onChange={(event) => updateField('preparationNotes', event.target.value)} />
                    {error('preparationNotes')}
                </div>
            </div>
        </Dialog>
    );
};

export default UnplannedVisitDialog;
